import * as React from 'react';
import { OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import {
    AppPlatform,
    CompletedStepsTrigger,
    ReleaseType,
    AppStoreType,
    ReleaseTypesForNonBlockingSteps,
    ReleaseStepBlockingConfig,
    SettingType,
} from '../types';
import Select from 'antd/lib/select';
import { RootState } from '../reducers/reducers';
import {
    selectReleaseStepsByReleaseType,
    selectWorkflowEnabledSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
} from '../selectors/selectors';
import { useWatch } from 'antd/lib/form/Form';
import { Form, Radio } from 'antd/lib';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { createAppSettingsRouteWithSettingType } from '../helpers/routeHelpers';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey } from '../components/styles/globalStyles';

interface CompletedStepsTriggerSettingsProps extends OrgComponentProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
}

export const CompletedStepsTriggerSettings: React.FC<CompletedStepsTriggerSettingsProps> = ({
    appId,
    platform,
    appStoreType,
}: CompletedStepsTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const trigger = useWatch<CompletedStepsTrigger>('completedStepsTrigger');
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const appSettingsRoute = createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.General);
    const releaseStepsByReleaseType = useSelector((state: RootState) => selectReleaseStepsByReleaseType(state, appId));
    const allReleaseSteps = releaseStepsByReleaseType[ReleaseType.Minor];
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const archetypeOptions = React.useMemo(() => {
        const allBlockingSteps = allReleaseSteps.filter(
            (step): boolean =>
                nonBlockingStepsConfigMap[ReleaseTypesForNonBlockingSteps.Standard][step] ===
                ReleaseStepBlockingConfig.Blocking,
        );
        return Object.values(allBlockingSteps).map((step) => {
            const isStepWorkflowsEnabled = workflowEnabledSteps[step];
            return {
                value: step,
                label: displayTextForReleaseStepType(step, platform, appStoreType, isStepWorkflowsEnabled),
            };
        });
    }, [nonBlockingStepsConfigMap, allReleaseSteps, workflowEnabledSteps, platform, appStoreType]);

    const requiredStepsValidator = React.useCallback(async (_, values) => {
        if (!values?.length) {
            return Promise.reject(
                new Error('Please adjust your selection or select a required release steps to be complete (green).'),
            );
        }
        return Promise.resolve();
    }, []);

    React.useEffect(() => {
        if (trigger) {
            form.setFieldValue('completedStepsTrigger', {
                ...trigger,
                hasCustomRequiredSteps: trigger.hasCustomRequiredSteps ?? false,
                requiredSteps: trigger.requiredSteps ?? [],
            });
        }
    }, [form, trigger]);

    return (
        <div>
            <FormItem
                name={['completedStepsTrigger', 'hasCustomRequiredSteps']}
                style={{ marginBottom: 0, marginTop: 0 }}
            >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Radio value={false}>
                        Require all previous blocking release steps{' '}
                        <Tooltip
                            title={
                                <div>
                                    Go to<Link to={appSettingsRoute}> App Settings </Link>to configure blocking and
                                    non-blocking steps
                                </div>
                            }
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 4 }} />
                        </Tooltip>
                    </Radio>
                    <Radio value={true}>
                        Require only selected blocking release steps{' '}
                        <Tooltip
                            title={
                                <div>
                                    Go to<Link to={appSettingsRoute}> App Settings </Link>to configure blocking and
                                    non-blocking steps
                                </div>
                            }
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 4 }} />
                        </Tooltip>
                    </Radio>
                </Radio.Group>
            </FormItem>
            {trigger?.hasCustomRequiredSteps && (
                <FormItem
                    required
                    name={['completedStepsTrigger', 'requiredSteps']}
                    rules={[{ validator: requiredStepsValidator }]}
                    style={{ marginTop: 16, marginBottom: 0 }}
                >
                    <Select mode="multiple" options={archetypeOptions} placeholder="Select required steps" />
                </FormItem>
            )}
        </div>
    );
};
